import React, { useEffect, useState } from 'react';
import { adminApiService } from '../../services/adminApiService';

interface GlobalRiskConfig {
  id?: number;
  maxDailyDrawdownPct: number;
  maxMonthlyDrawdownPct: number;
  maxTotalNotionalUsd: number;
  maxConcurrentBots: number;
  circuitBreakerEnabled: boolean;
  emergencyStop: boolean;
}

interface RiskViolation {
  id: number;
  botId?: number;
  violationType: string;
  description: string;
  currentValue: number;
  limitValue: number;
  actionTaken: string;
  resolved: boolean;
  createdAt: string;
  resolvedAt?: string;
}

const RiskManagementPanel: React.FC = () => {
  const [config, setConfig] = useState<GlobalRiskConfig>({
    maxDailyDrawdownPct: 2.0,
    maxMonthlyDrawdownPct: 8.0,
    maxTotalNotionalUsd: 100000,
    maxConcurrentBots: 10,
    circuitBreakerEnabled: true,
    emergencyStop: false
  });
  const [violations, setViolations] = useState<RiskViolation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      const response = await adminApiService.getGlobalRiskConfig();
      if (response.success && response.data) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch risk config:', err);
    }
  };

  const fetchViolations = async () => {
    try {
      const response = await adminApiService.getRecentRiskViolations(24);
      if (response.success && response.data) {
        setViolations(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch violations:', err);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchViolations();
  }, []);

  const handleConfigUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await adminApiService.updateGlobalRiskConfig(config);
      if (response.success) {
        setSuccess('Risk configuration updated successfully');
        fetchConfig();
      } else {
        setError(response.error || 'Failed to update configuration');
      }
    } catch (err) {
      setError('Failed to update configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyStop = async () => {
    if (!window.confirm('Are you sure you want to emergency stop all trading? This will stop all running bots immediately.')) {
      return;
    }

    try {
      const response = await adminApiService.emergencyStopAll();
      if (response.success) {
        setSuccess('Emergency stop activated - all trading has been halted');
        fetchConfig();
      } else {
        setError('Failed to activate emergency stop');
      }
    } catch (err) {
      setError('Failed to activate emergency stop');
    }
  };

  const handleResumeTrading = async () => {
    if (!window.confirm('Are you sure you want to resume trading? This will allow bots to start again.')) {
      return;
    }

    try {
      const response = await adminApiService.resumeTrading();
      if (response.success) {
        setSuccess('Trading has been resumed');
        fetchConfig();
      } else {
        setError('Failed to resume trading');
      }
    } catch (err) {
      setError('Failed to resume trading');
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div>
      <div className="admin-content-header">
        <h2 className="content-title">Risk Management</h2>
        <div className="content-breadcrumb">Admin Dashboard / Risk Management</div>
      </div>

      <div className="admin-content-body">
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: 20 }}>
            {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            {success}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Risk Configuration */}
          <div className="admin-card">
            <h3>Global Risk Configuration</h3>
            <form onSubmit={handleConfigUpdate}>
              <div className="form-group">
                <label>Max Daily Drawdown (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={config.maxDailyDrawdownPct}
                  onChange={(e) => setConfig({...config, maxDailyDrawdownPct: parseFloat(e.target.value)})}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Max Monthly Drawdown (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={config.maxMonthlyDrawdownPct}
                  onChange={(e) => setConfig({...config, maxMonthlyDrawdownPct: parseFloat(e.target.value)})}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Max Total Notional (USD)</label>
                <input
                  type="number"
                  step="1000"
                  min="0"
                  value={config.maxTotalNotionalUsd}
                  onChange={(e) => setConfig({...config, maxTotalNotionalUsd: parseFloat(e.target.value)})}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Max Concurrent Bots</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={config.maxConcurrentBots}
                  onChange={(e) => setConfig({...config, maxConcurrentBots: parseInt(e.target.value)})}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={config.circuitBreakerEnabled}
                    onChange={(e) => setConfig({...config, circuitBreakerEnabled: e.target.checked})}
                  />
                  {' '}Circuit Breaker Enabled
                </label>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Configuration'}
              </button>
            </form>
          </div>

          {/* Emergency Controls */}
          <div className="admin-card">
            <h3>Emergency Controls</h3>
            <div style={{ marginBottom: 20 }}>
              <strong>Status:</strong>{' '}
              <span style={{ color: config.emergencyStop ? 'red' : 'green' }}>
                {config.emergencyStop ? 'EMERGENCY STOP ACTIVE' : 'TRADING ACTIVE'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
              <button
                onClick={handleEmergencyStop}
                className="btn btn-danger"
                disabled={config.emergencyStop}
              >
                Emergency Stop All Trading
              </button>

              <button
                onClick={handleResumeTrading}
                className="btn btn-success"
                disabled={!config.emergencyStop}
              >
                Resume Trading
              </button>
            </div>

            <div style={{ marginTop: 20, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 4 }}>
              <small>
                <strong>Warning:</strong> Emergency stop will immediately halt all trading activity. 
                Use only in critical situations. Resume trading only after resolving the underlying issue.
              </small>
            </div>
          </div>
        </div>

        {/* Risk Violations */}
        <div className="admin-card" style={{ marginTop: 24 }}>
          <h3>Recent Risk Violations (Last 24 Hours)</h3>
          {violations.length === 0 ? (
            <p>No risk violations in the last 24 hours.</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Bot ID</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Current Value</th>
                    <th>Limit</th>
                    <th>Action</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.map((violation) => (
                    <tr key={violation.id}>
                      <td>{formatTimestamp(violation.createdAt)}</td>
                      <td>{violation.botId || 'Global'}</td>
                      <td>{violation.violationType}</td>
                      <td>{violation.description}</td>
                      <td>{violation.currentValue.toFixed(2)}</td>
                      <td>{violation.limitValue.toFixed(2)}</td>
                      <td>{violation.actionTaken}</td>
                      <td>
                        <span style={{ color: violation.resolved ? 'green' : 'red' }}>
                          {violation.resolved ? 'Resolved' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskManagementPanel;
